/* eslint-disable no-undef */
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import React from 'react';
import PropTypes from 'prop-types';
import ToolboxItem from './ToolboxItem';
import ToggleButton from './ToggleButton';
import { NOTIFY_CLICK_MODE } from 'store/modules/toolbox/constants';
import { combineStyles } from 'store/modules/base/styles';

/**
 * Default style for disabled buttons.
 */
export const defaultDisabledButtonStyles = {
  iconStyle: {
    opacity: 0.5
  },
  labelStyle: {
    opacity: 0.5
  },
  style: undefined,
  underlayColor: undefined
};

/**
 * An abstract implementation of a button.
 */
export default class AbstractButton extends React.PureComponent {
  static defaultProps = {
    afterClick: undefined,
    disabledStyles: defaultDisabledButtonStyles,
    showLabel: true,
    styles: undefined,
    toggledStyles: undefined,
    tooltipPosition: 'right',
    visible: true
  };

  /**
   * Initializes a new {@code AbstractButton} instance.
   *
   * @param {Props} props - The React {@code Component} props to initialize
   * the new {@code AbstractButton} instance with.
   */
  constructor(props) {
    super(props);

    // Bind event handlers so they are only bound once per instance.
    this._onClick = this._onClick.bind(this);
    this.state = { isOpen: false };
  }

  /**
   * Helper function to be implemented by subclasses, which should be used
   * to handle a key being down.
   *
   * @protected
   * @returns {void}
   */
  _onKeyDown() {
    // To be implemented by subclass.
  }

  /**
   * Helper function to be implemented by subclasses, which should be used
   * to handle the button being clicked / pressed.
   *
   * @protected
   * @returns {void}
   */
  _handleClick() {
    // To be implemented by subclass.
  }

  /**
   * Helper function to be implemented by subclasses, which may return a
   * new React Element to be appended at the end of the button.
   *
   * @protected
   * @returns {ReactElement|null}
   */
  _getElementAfter() {
    return null;
  }

  /**
   * Helper function to be implemented by subclasses, which may return a
   * new React Element to be appended at the end of the button.
   *
   * @protected
   * @returns {ReactElement|null}
   */
  _getDialogChildren() {
    return null;
  }

  /**
   * Gets the current icon, taking the toggled state into account. If no
   * toggled icon is provided, the regular icon will also be used in the
   * toggled state.
   *
   * @private
   * @returns {string}
   */
  _getIcon() {
    return (this._isToggled() ? this.toggledIcon : this.icon) || this.icon;
  }

  /**
   * Gets the current label, taking the toggled state into account. If no
   * toggled label is provided, the regular label will also be used in the
   * toggled state.
   *
   * @private
   * @returns {string}
   */
  _getLabel() {
    return (this._isToggled() ? this.toggledLabel : this.label) || this.label;
  }

  /**
   * Gets the current styles, taking the toggled state into account. If no
   * toggled styles are provided, the regular styles will also be used in the
   * toggled state.
   *
   * @private
   * @returns {?Styles}
   */
  _getStyles() {
    const { disabledStyles, styles, toggledStyles } = this.props;
    const buttonStyles = (this._isToggled() ? toggledStyles : styles) || styles;

    if (this._isDisabled() && buttonStyles && disabledStyles) {
      return {
        iconStyle: combineStyles(
          buttonStyles.iconStyle,
          disabledStyles.iconStyle
        ),
        labelStyle: combineStyles(
          buttonStyles.labelStyle,
          disabledStyles.labelStyle
        ),
        style: combineStyles(buttonStyles.style, disabledStyles.style),
        underlayColor:
          disabledStyles.underlayColor || buttonStyles.underlayColor
      };
    }

    return buttonStyles;
  }

  /**
   * Get the tooltip to display when hovering over the button.
   *
   * @private
   * @returns {string}
   */
  _getTooltip() {
    return this._isToggled() ? this.tooltip || '' : '';
  }

  /**
   * Helper function to be implemented by subclasses, which must return a
   * boolean value indicating if this button is disabled or not.
   *
   * @protected
   * @returns {boolean}
   */
  _isDisabled() {
    return false;
  }

  /**
   * Helper function to be implemented by subclasses, which must return a
   * {@code boolean} value indicating if this button is toggled or not or
   * undefined if the button is not toggleable.
   *
   * @protected
   * @returns {?boolean}
   */
  _isToggled() {
    return undefined;
  }

  /**
   * Handles clicking / pressing the button.
   *
   * @param {Object} e - Event.
   * @private
   * @returns {void}
   */
  _onClick(e) {
    const { afterClick, handleClick, notifyMode, buttonKey } = this.props;

    if (this._getDialogChildren()) {
      this.setState({ isOpen: true });
    }
    if (typeof APP !== 'undefined' && notifyMode) {
      APP.API.notifyToolbarButtonClicked(
        buttonKey,
        notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY
      );
    }

    if (notifyMode !== NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY) {
      if (handleClick) {
        handleClick();
      }

      this._handleClick();
    }

    afterClick && afterClick(e);

    // blur after click to release focus from button to allow PTT.
    e?.currentTarget?.blur && e.currentTarget.blur();
  }

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {React$Node}
   */
  render() {
    const props = {
      ...this.props,
      accessibilityLabel: this.accessibilityLabel,
      elementAfter: this._getElementAfter(),
      type: this.type,
      transparent: this.props._userIdleMode,
      icon: this._getIcon(),
      label: this._getLabel(),
      labelProps: this.labelProps,
      styles: this._getStyles(),
      toggled: this._isToggled(),
      tooltip: this._getTooltip()
    };
    const isMobile = props.clientWidth < 500 ? 'bottom' : 'left';

    if (props.isSwitchButton) {
      return (
        <ToggleButton
          disabled={this._isDisabled()}
          onClick={this._onClick}
          onKeyDown={this._onKeyDown}
          {...props}
        />
      );
    }
    const toggleDrawer = (open) => (event) => {
      if (
        event &&
        event.type === 'keydown' &&
        (event.key === 'Tab' || event.key === 'Shift')
      ) {
        return;
      }
      this.setState({ isOpen: open });
    };
    const style =
      this.label === 'toolbar.groups'
        ? {
            background: 'rgba(0, 0, 0, 0.8)',
            width: '100%'
          }
        : { background: 'rgba(0,0,0,0.8)' };

    return (
      <React.Fragment key={isMobile}>
        {this.state.isOpen && (
          <Drawer
            PaperProps={{
              sx: style
            }}
            anchor={isMobile}
            onClose={toggleDrawer(false)}
            open={this.state.isOpen}>
            {this._getDialogChildren()}
          </Drawer>
        )}
        {props.showLabel ? (
          <ToolboxItem
            disabled={this._isDisabled()}
            onClick={this._onClick}
            onKeyDown={this._onKeyDown}
            {...props}
          />
        ) : (
          <div
            style={{
              margin: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
            <ToolboxItem
              disabled={this._isDisabled()}
              onClick={this._onClick}
              onKeyDown={this._onKeyDown}
              {...props}
            />
            <Typography
              variant="labelSmall"
              style={{
                color: this.props._userIdleMode
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'white',
                padding: '5px',
                textAlign: 'center',
                textShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)'
              }}>
              {props.t(this.label)}
            </Typography>
          </div>
        )}
      </React.Fragment>
    );
  }
}

AbstractButton.propTypes = {
  /**
   * Function to be called after the click handler has been processed.
   */
  afterClick: PropTypes.func,

  /**
   * The button's key.
   */
  buttonKey: PropTypes.string,

  /**
   * Whether or not the button is displayed in a context menu.
   */
  contextMenu: PropTypes.bool,

  /**
   * An extra class name to be added at the end of the element's class name
   * in order to enable custom styling.
   */
  customClass: PropTypes.string,

  /**
   * Extra styles which will be applied in conjunction with `styles` or
   * `toggledStyles` when the button is disabled;.
   */
  disabledStyles: PropTypes.object,

  /**
   * External handler for click action.
   */
  handleClick: PropTypes.func,

  /**
   * Notify mode for `toolbarButtonClicked` event -
   * whether to only notify or to also prevent button click routine.
   */
  notifyMode: PropTypes.string,
  id: PropTypes.string,

  /**
   * Whether to show the label or not.
   */
  showLabel: PropTypes.bool,
  isSwitchButton: PropTypes.bool,
  _userIdleMode: PropTypes.bool,

  /**
   * Collection of styles for the button.
   */
  styles: PropTypes.object,

  /**
   * Collection of styles for the button, when in toggled state.
   */
  toggledStyles: PropTypes.object,

  /**
   * From which direction the tooltip should appear, relative to the button.
   */
  tooltipPosition: PropTypes.string,

  /**
   * Whether this button is visible or not.
   */
  visible: PropTypes.bool
};
